import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { isPlatformAdmin } from '../lib/roles';
import { ArrowDownLeft, ArrowUpRight, Shield } from 'lucide-react';

const TransactionsPage = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminUserId = searchParams.get('user');
  const viewingOther = Boolean(adminUserId && isPlatformAdmin(user?.role) && adminUserId !== user?.id);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const url = viewingOther
          ? `${API}/admin/users/${adminUserId}/transactions`
          : `${API}/user/transactions`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load transactions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, adminUserId, viewingOther, navigate]);

  const [typeFilter, setTypeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const rows = (data?.transactions || []).filter((t) => {
    if (typeFilter !== 'all' && (t.type || '') !== typeFilter) return false;
    if (query && !`${t.title || ''} ${t.reason || ''} ${t.type || ''}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const who = viewingOther ? (data?.user?.name || 'User') : 'You';
  const isDepositRow = (t) =>
    t.is_deposit || ['deposit_topup', 'deposit_deduction', 'deposit_withdrawal', 'late_fee', 'credit'].includes(t.type);

  const rowTone = (t) => {
    const dir = t.display_direction || t.direction;
    if (dir === 'wallet_in' || t.type === 'deposit_topup') return 'wallet';
    if (dir === 'wallet_out' || ['deposit_deduction', 'late_fee'].includes(t.type)) return 'wallet_out';
    if (t.type === 'deposit_withdrawal') return 'wallet_out';
    if (dir === 'credit') return 'in';
    return 'out';
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold mb-1" style={{ color: 'var(--primary)' }}>Transactions</h1>
        <p className="text-sm text-gray-500 mb-6">
          {viewingOther
            ? `Ledger for ${who}`
            : 'Deposit stays in your wallet until a withdrawal is paid to your bank (7–10 business days). Paid is completed rentals.'}
        </p>

        {loading && <p className="text-gray-600">Loading…</p>}
        {error && <p className="text-red-600 text-sm">{error}</p>}

        {!loading && !error && (
          <>
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title or reason"
                className="border rounded-lg px-3 py-2 text-sm flex-1"
              />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All types</option>
                <option value="rental">Rentals</option>
                <option value="deposit_topup">Deposit added</option>
                <option value="deposit_withdrawal">Withdrawals</option>
                <option value="deposit_bank_payout">Paid to bank</option>
                <option value="deposit_deduction">Deposit deducted</option>
                <option value="earning">Earnings</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Deposit
                </p>
                <p className="text-lg font-bold" style={{ color: 'var(--primary)' }}>
                  ₹{Math.round(data?.deposit_balance || 0)}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">Held in wallet</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-xs text-gray-500">Paid</p>
                <p className="text-lg font-bold text-red-700">₹{Math.round(data?.money_out || 0)}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">Completed rentals</p>
              </div>
            </div>

            <p className="text-xs text-gray-400 mb-2">{rows.length} transaction{rows.length === 1 ? '' : 's'}</p>

            {rows.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-gray-500 shadow-sm">
                No completed payments yet.
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm divide-y">
                {rows.map((t) => {
                  const voided = Boolean(t.voided);
                  const tone = voided ? 'void' : rowTone(t);
                  const iconWrap =
                    voided
                      ? 'bg-gray-100 text-gray-500'
                      : tone === 'wallet'
                      ? 'bg-blue-50 text-blue-800'
                      : tone === 'wallet_out'
                        ? 'bg-orange-50 text-orange-800'
                        : tone === 'in'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700';
                  const amountClass =
                    tone === 'wallet' || tone === 'wallet_out'
                      ? ''
                      : tone === 'in'
                        ? 'text-green-700'
                        : 'text-red-700';
                  const amountStyle =
                    tone === 'wallet' || tone === 'wallet_out' ? { color: 'var(--primary)' } : undefined;
                  const prefix = voided ? '' : tone === 'in' ? '+' : tone === 'out' ? '−' : '';
                  return (
                    <div key={t.id || t.unique_key} className="flex items-start justify-between gap-3 p-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`mt-0.5 rounded-full p-1.5 ${iconWrap}`}>
                          {tone === 'wallet' || tone === 'wallet_out' ? (
                            <Shield className="h-4 w-4" />
                          ) : tone === 'in' ? (
                            <ArrowDownLeft className="h-4 w-4" />
                          ) : (
                            <ArrowUpRight className="h-4 w-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{t.title || t.type}</p>
                          <p className="text-xs text-gray-500">{t.reason || '—'}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {t.created_at ? new Date(t.created_at).toLocaleString() : ''}
                            {isDepositRow(t) ? ' · Held in deposit' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className={`font-bold tabular-nums ${voided ? 'text-gray-400 line-through' : amountClass}`} style={voided ? undefined : amountStyle}>
                          {prefix}₹{Math.round(t.amount || 0)}
                        </p>
                        {isDepositRow(t) && t.deposit_running != null && (
                          <p className="text-[10px] text-gray-400">Wallet ₹{Math.round(t.deposit_running)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TransactionsPage;
